import { useState, useEffect } from "react";
import { Volume2 } from "lucide-react";

// Typewriter effect for assistant messages
const TypewriterText = ({ text, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isDone, setIsDone] = useState(false);
  
  useEffect(() => {
    if (!text) return;
    const words = text.split(' ');
    let currentIndex = 0;
    setDisplayedText('');
    setIsDone(false);
    
    const interval = setInterval(() => {
      currentIndex++;
      setDisplayedText(words.slice(0, currentIndex).join(' '));
      if (currentIndex >= words.length) {
        clearInterval(interval);
        setIsDone(true);
        if (onComplete) onComplete();
      }
    }, 60);
    
    return () => clearInterval(interval);
  }, [text]);
  
  return <span>{displayedText}{!isDone && <span className="animate-pulse">|</span>}</span>;
};

const ChatMessage = ({ message, isUser, poodleImage, userAvatar, userAvatarShape, learningTower, onSpeak, isSpeaking, bigText, isTyping, onTypingComplete }) => {
  // Match the shape the kid picked in Avatar Maker (circle / square).
  // Default to circle for safety / legacy avatars saved before this prop existed.
  const avatarShapeClass =
    userAvatarShape === "frame-square" ? "rounded-2xl" : "rounded-full";

  // When the kid is in a Learning Tower, mount a tiny tower SVG on top of
  // the poodle's head — like a "subject brain" for the poodle. Cute touch
  // requested by Louise.
  const TowerBrain = learningTower?.Comp;

  return (
  <div 
    className={`flex items-end gap-3 animate-fade-in-up ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
  >
    {!isUser && (
      <div className="relative flex-shrink-0">
        <img 
          src={poodleImage} 
          alt="Poodle friend" 
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-3 border-white shadow-md"
        />
        {TowerBrain && (
          <div
            className="absolute -top-5 -right-2 w-7 h-10 sm:w-8 sm:h-12 pointer-events-none lt-bob-2 drop-shadow-[0_2px_3px_rgba(0,0,0,0.25)]"
            title={`${learningTower.label} brain on!`}
            data-testid={`brain-tower-${learningTower.key}`}
          >
            <TowerBrain />
          </div>
        )}
      </div>
    )}
    {isUser && userAvatar && (
      <img
        src={userAvatar}
        alt="Your avatar"
        data-testid="user-avatar"
        className={`w-12 h-12 sm:w-14 sm:h-14 ${avatarShapeClass} object-cover border-3 border-white shadow-md flex-shrink-0`}
      />
    )}
    <div 
      className={`max-w-[80%] p-4 sm:p-5 ${
        isUser 
          ? 'bg-[#E6F0F9] border-2 border-[#C9DFE9] rounded-3xl rounded-tr-lg' 
          : 'bg-white border-2 border-[#EAE3D2] rounded-3xl rounded-tl-lg shadow-sm'
      }`}
    >
      <p className={`${bigText ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'} leading-relaxed text-[#4A4A4A]`}>
        {isTyping ? <TypewriterText text={message} onComplete={onTypingComplete} /> : message}
      </p>
      {!isUser && onSpeak && !isTyping && (
        <button
          onClick={() => onSpeak(message)}
          data-testid="listen-button"
          className={`mt-2 flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-all ${
            isSpeaking 
              ? 'bg-[#FFF7D6] text-[#B89823]' 
              : 'bg-[#F3F0E6] text-[#9A9A9A] hover:bg-[#E8E5DC] hover:text-[#6B6B6B]'
          }`}
        >
          <Volume2 className={`w-3 h-3 ${isSpeaking ? 'animate-pulse' : ''}`} />
          {isSpeaking ? 'Playing...' : 'Listen'}
        </button>
      )}
    </div>
  </div>
  );
};

export default ChatMessage;
